import React, {useCallback, useContext, useMemo} from "react";
import {DataGridMiscContext, DataGridScrollContext, DataGridStateContext, DEFAULT_DATA_GRID_MIN_ROW_HEIGHT,} from "../../../index";
import classnames from "classnames";
import {DataGridColumnPinnedTypes, DataGridExoticColumnFields, DefaultDataGridCell} from "../../../models";
import DataGridBodyRow from "./row";
import ColumnHeightProbe from "./column-height-probe";
import DataGridUtils from "../../../core/services/utils";
import {buildCellHighlightPlans, highlightMapKey} from "../../../core/services/utils/highlight";
import {resolveDataGridVirtualization, shouldVirtualizeDataGridRows} from "../../../core/services/utils/virtualization";
import {DefaultDataGridPageSize} from "../../../models";
import {useColumnVirtualizer, useDynamicRowVirtualizer} from "../../../../../ui/hooks/use-row-virtualizer";
import {useVirtualColumnHeightCache, useVirtualColumnHeightProbe} from "./virtual-column-heights";

/**
 * @param {DataGridRow[]} rows
 * @param {number | undefined} layoutRectWidth
 * @param {Function} startScrolling
 * @param {number} avgRowHeight
 * @param {number} minRowHeight
 * @param {function(number): void | undefined} onVirtualRowHeightSample
 * @return {JSX.Element}
 * @constructor
 */
const DataGridBody = ({
                          rows,
                          layoutRectWidth,
                          startScrolling,
                          avgRowHeight,
                          minRowHeight = DEFAULT_DATA_GRID_MIN_ROW_HEIGHT,
                          onVirtualRowHeightSample,
                      }) => {
    const misc = useContext(DataGridMiscContext);
    const {classNames} = misc;
    const {rowsEnabled, columnsEnabled} = resolveDataGridVirtualization(misc);
    const maxVisibleRows = misc.visibleRows ?? DefaultDataGridPageSize;
    const {selectedRows, allRowsSelected, excludedRows, columns, density, group} = useContext(DataGridStateContext);
    const {getLayoutScrollParent} = useContext(DataGridScrollContext);
    const containerRef = React.useRef(null);

    const stickyTopContainerRef = React.useRef(null);
    const stickyBottomContainerRef = React.useRef(null);

    const [stickyTopHeight, setStickyTopHeight] = React.useState(0);
    const [stickyBottomHeight, setStickyBottomHeight] = React.useState(0);

    const {stickyTopRows, stickyBottomRows, middleRows} = useMemo(() => {
        if (!rowsEnabled) {
            return {stickyTopRows: [], stickyBottomRows: [], middleRows: rows ?? []};
        }
        const stickyTopRows = rows?.filter(r => r?.stickToTop) ?? [];
        const stickyBottomRows = rows?.filter(r => r?.stickToBottom) ?? [];
        const middleRows = rows?.filter(r => !r?.stickToTop && !r?.stickToBottom) ?? [];
        return {stickyTopRows, stickyBottomRows, middleRows};
    }, [rows, rowsEnabled]);

    const shouldVirtualizeRows = shouldVirtualizeDataGridRows(rowsEnabled, middleRows.length, maxVisibleRows);

    React.useEffect(() => {
        if (!shouldVirtualizeRows) return;

        const topEl = stickyTopContainerRef.current;
        const bottomEl = stickyBottomContainerRef.current;
        const ro = new ResizeObserver(() => {
            const th = topEl ? topEl.getBoundingClientRect().height : 0;
            const bh = bottomEl ? bottomEl.getBoundingClientRect().height : 0;
            setStickyTopHeight(th);
            setStickyBottomHeight(bh);
        });
        if (topEl) ro.observe(topEl);
        if (bottomEl) ro.observe(bottomEl);
        const th0 = topEl ? topEl.getBoundingClientRect().height : (stickyTopRows.length * Math.max(minRowHeight, avgRowHeight || 0));
        const bh0 = bottomEl ? bottomEl.getBoundingClientRect().height : (stickyBottomRows.length * Math.max(minRowHeight, avgRowHeight || 0));
        setStickyTopHeight(th0);
        setStickyBottomHeight(bh0);
        return () => ro.disconnect();
    }, [shouldVirtualizeRows, stickyTopRows.length, stickyBottomRows.length, avgRowHeight, minRowHeight]);

    /**
     * Compares cell text for columns that opted into diff or similarity highlighting.
     * Uses every row passed to the body so virtualization does not change the highlights while scrolling.
     * @type {Map<string, DataGridCellHighlight>}
     */
    const highlightPlans = useMemo(() => buildCellHighlightPlans(
        rows,
        columns,
        (row) => (allRowsSelected && !excludedRows.find(e => e.key === row.key)) ||
            (!allRowsSelected && !!selectedRows.find(e => e.key === row.key)),
    ), [rows, columns, allRowsSelected, excludedRows, selectedRows]);

    const createCells = useCallback((columnsForCells, row) => {
        return columnsForCells?.map((column, index, array) => {
            const firstRightPinned =
                (index === 0 && column.pinnedType === DataGridColumnPinnedTypes.right) ||
                (index > 0 && !array[index - 1].pinned && column.pinnedType === DataGridColumnPinnedTypes.right);
            const lastLeftPinned =
                ((index + 1 === array.length) && column.pinnedType === DataGridColumnPinnedTypes.left) ||
                (
                    (array.length > index + 1 && array[index + 1]?.pinned === false)
                    && column.pinnedType === DataGridColumnPinnedTypes.left
                );

            let content = row.cells[column.name];
            if (React.isValidElement(content) || typeof content !== 'object') {
                content = {
                    value: content,
                    editableValue: null,
                    editable: false,
                };
            } else {
                content = {
                    ...DefaultDataGridCell,
                    ...(content ?? {}),
                };
            }

            return ({
                column: column,
                firstRightPinned: firstRightPinned,
                lastLeftPinned: lastLeftPinned,
                content: content,
                showGroupToggler: false,
                highlight: highlightPlans.get(highlightMapKey(row.key, column.name)),
            });
        }) ?? [];
    }, [highlightPlans]);

    const _visibleColumns = useMemo(() =>
        columns.filter(e =>
            e.visible &&
            e.name !== DataGridExoticColumnFields.detailedPanel,
        ), [columns]);

    const visibleColumns = useMemo(() => {
        return DataGridUtils.getVisibleColumnsCalculatedWidths(_visibleColumns, layoutRectWidth);
    }, [_visibleColumns, layoutRectWidth]);

    const getColWidth = React.useCallback((c) => Math.max(c.width?.size ?? 0, c.width?.minWidth ?? 0), []);
    const isPinnedLeft = React.useCallback((c) => c.pinnedType === DataGridColumnPinnedTypes.left, []);
    const isPinnedRight = React.useCallback((c) => c.pinnedType === DataGridColumnPinnedTypes.right, []);

    const colV = useColumnVirtualizer({
        enabled: columnsEnabled,
        columns: visibleColumns,
        getScrollParent: getLayoutScrollParent,
        getColumnWidth: getColWidth,
        isPinnedLeft,
        isPinnedRight,
        overscanPx: 240,
    });

    const columnsForCells = React.useMemo(
        () => [...colV.leftPinned, ...colV.visibleCenter, ...colV.rightPinned],
        [colV.leftPinned, colV.visibleCenter, colV.rightPinned],
    );

    const renderedColCount = useMemo(() => {
        if (!columnsEnabled) {
            return visibleColumns?.length ?? 0;
        }
        let count = colV.leftPinned.length + colV.visibleCenter.length + colV.rightPinned.length;
        if (colV.leftSpacerWidth > 0) count += 1;
        if (colV.rightSpacerWidth > 0) count += 1;
        return count;
    }, [columnsEnabled, visibleColumns?.length, colV]);

    const visibleCenterColumnKeys = useMemo(
        () => columnsEnabled ? colV.visibleCenter.map(c => String(c.name)) : undefined,
        [columnsEnabled, colV.visibleCenter],
    );

    const recursiveGroupRows = useCallback((rowsToGroup, groupLevels, currentLevel = 0) => {
        if (currentLevel >= groupLevels.length) {
            return rowsToGroup;
        }

        const {columnName, showOnColumn} = groupLevels[currentLevel];

        const rowGroups = rowsToGroup.reduce((obj, row) => {
            const groupValue = row._cellsRaw[columnName]?.groupValue;
            if (groupValue === null || typeof groupValue === "undefined") {
                return obj;
            }
            obj[groupValue] = [
                ...(obj[groupValue] ?? []),
                row,
            ];
            return obj;
        }, {}) ?? {};

        const rowGroupIncluded = Object.fromEntries(Object.keys(rowGroups).map(e => [e, false]));

        return rowsToGroup.reduce((array, row) => {
            const groupValue = row._cellsRaw[columnName]?.groupValue;
            if (groupValue === null || typeof groupValue === "undefined") {
                array.push(row);
                return array;
            }
            if (!rowGroupIncluded[groupValue]) {
                const groupedRows = rowGroups[groupValue].filter(e => e.key !== row.key) ?? [];
                const nestedGroupedRows = recursiveGroupRows(groupedRows, groupLevels, currentLevel + 1);
                array.push({
                    ...row,
                    cells: row.cells.map(e => ({
                        ...e,
                        showGroupToggler: e.column.name === showOnColumn,
                        groupedRowsLength: nestedGroupedRows.length,
                    })),
                    groupedRows: nestedGroupedRows,
                });
                rowGroupIncluded[groupValue] = true;
                return array;
            }
            return array;
        }, []);
    }, []);

    const estimateRowHeight = Math.max(minRowHeight, avgRowHeight || 0);

    const rowHeightCache = useVirtualColumnHeightCache({
        enabled: columnsEnabled,
        density,
        minRowHeight,
        allRows: rows,
        sampleRows: middleRows,
        sampleLimit: maxVisibleRows,
        onHeightSample: onVirtualRowHeightSample,
        getColWidth,
        columns: visibleColumns,
    });

    const rowV = useDynamicRowVirtualizer({
        enabled: shouldVirtualizeRows,
        data: middleRows,
        estimateRowHeight,
        minRowHeight,
        overscan: 6,
        getScrollParent: getLayoutScrollParent,
        getItemKey: (r) => r.key,
        stickyHeaderHeight: shouldVirtualizeRows ? stickyTopHeight : 0,
        stickyFooterHeight: shouldVirtualizeRows ? stickyBottomHeight : 0,
        heightRevision: rowHeightCache.heightRevision,
        getRowHeight: columnsEnabled && shouldVirtualizeRows ? rowHeightCache.getRowHeight : undefined,
        heightResetToken: columnsEnabled ? density : undefined,
        preserveLockedRowHeight: columnsEnabled && shouldVirtualizeRows,
    });

    const usableRows = useMemo(() => {
        if (!shouldVirtualizeRows) return middleRows;
        return rowV.items.map((e, i) => ({
            ...e,
            key: rowV.keyFor(i),
            ref: rowV.rowRef(i),
        }));
    }, [shouldVirtualizeRows, middleRows, rowV.items, rowV.keyFor, rowV.rowRef]);

    const getVisibleRows = useCallback((rowsToRender) => {
        const groups = Array.isArray(group) ? group : !group ? undefined : [group];
        const cellsColumns = columnsEnabled ? columnsForCells : visibleColumns;

        let result = rowsToRender?.map(row => ({
            ...row,
            selected: (allRowsSelected && !excludedRows.find(e => e.key === row.key)) ||
                (!allRowsSelected && !!selectedRows.find(e => e.key === row.key)),
            detailedPanel: row.cells[DataGridExoticColumnFields.detailedPanel],
            colCount: renderedColCount,
            cells: createCells(cellsColumns, row),
            _cellsRaw: row.cells,
        })) ?? [];

        if (!groups?.length) {
            return result;
        }

        result = recursiveGroupRows(result, groups);
        return result;
    }, [
        allRowsSelected,
        excludedRows,
        selectedRows,
        group,
        createCells,
        recursiveGroupRows,
        columnsForCells,
        visibleColumns,
        columnsEnabled,
        renderedColCount,
    ]);

    const visibleMiddleRows = useMemo(() => getVisibleRows(usableRows), [getVisibleRows, usableRows]);
    const visibleTopStickyRows = useMemo(() => getVisibleRows(stickyTopRows), [getVisibleRows, stickyTopRows]);
    const visibleBottomStickyRows = useMemo(() => getVisibleRows(stickyBottomRows), [getVisibleRows, stickyBottomRows]);

    const [columnProbeScroll, setColumnProbeScroll] = React.useState({top: 0, height: 0});

    /**
     * Tracks the scrollport so column-only virtualization probes the visible rows, not the whole list.
     */
    React.useEffect(() => {
        if (!columnsEnabled || shouldVirtualizeRows) {
            return undefined;
        }
        const el = getLayoutScrollParent();
        if (!el) {
            return undefined;
        }
        let frame = 0;
        const sync = () => {
            window.cancelAnimationFrame(frame);
            frame = window.requestAnimationFrame(() => {
                setColumnProbeScroll({top: el.scrollTop, height: el.clientHeight});
            });
        };
        sync();
        el.addEventListener("scroll", sync, {passive: true});
        return () => {
            el.removeEventListener("scroll", sync);
            window.cancelAnimationFrame(frame);
        };
    }, [columnsEnabled, shouldVirtualizeRows, getLayoutScrollParent, middleRows.length]);

    const heightProbeRows = useMemo(() => {
        if (!columnsEnabled) {
            return [];
        }
        if (shouldVirtualizeRows) {
            return [...visibleTopStickyRows, ...visibleMiddleRows, ...visibleBottomStickyRows];
        }
        const estimate = Math.max(minRowHeight, avgRowHeight || minRowHeight);
        const viewport = columnProbeScroll.height || estimate * maxVisibleRows;
        const start = Math.max(0, Math.floor(columnProbeScroll.top / estimate) - 6);
        const end = Math.min(visibleMiddleRows.length, Math.ceil((columnProbeScroll.top + viewport) / estimate) + 6);
        return [...visibleTopStickyRows, ...visibleMiddleRows.slice(start, end), ...visibleBottomStickyRows];
    }, [
        columnsEnabled,
        shouldVirtualizeRows,
        visibleTopStickyRows,
        visibleMiddleRows,
        visibleBottomStickyRows,
        minRowHeight,
        avgRowHeight,
        maxVisibleRows,
        columnProbeScroll,
    ]);

    const activeHeightProbe = useVirtualColumnHeightProbe({
        enabled: columnsEnabled,
        probeRows: heightProbeRows,
        columns: visibleColumns,
        cellCacheRef: rowHeightCache.cellCacheRef,
        cacheEpoch: rowHeightCache.cacheEpoch,
        getColWidth,
        commitMeasurements: rowHeightCache.commitMeasurements,
        containerRef,
        density,
        mountedColumnKey: visibleCenterColumnKeys?.join("|") ?? "",
    });

    const rowVirtProps = {
        leadingSpacerWidth: columnsEnabled ? colV.leftSpacerWidth : 0,
        trailingSpacerWidth: columnsEnabled ? colV.rightSpacerWidth : 0,
        visibleCenterColumnKeys,
        estimateRowHeight,
        minRowHeight,
        columnsEnabled,
        getLockedRowHeight: columnsEnabled ? rowHeightCache.getLockedRowHeight : undefined,
    };

    return (
        <>
            <tbody className={classnames('data-grid-body', classNames.body)} ref={containerRef}>
            {
                rowsEnabled && visibleTopStickyRows.length > 0 && (
                    <tr aria-hidden style={{position: 'sticky', top: 0, zIndex: 14, padding: 0}}>
                        <td colSpan={renderedColCount || 1} style={{padding: 0, border: 0}}>
                            <table style={{width: '100%', borderCollapse: 'separate'}}>
                                <tbody ref={stickyTopContainerRef}>
                                {visibleTopStickyRows.map(row => (
                                    <DataGridBodyRow
                                        key={`sticky-top-${row.key}`}
                                        row={row}
                                        density={density}
                                        startScrolling={startScrolling}
                                        {...rowVirtProps}
                                    />
                                ))}
                                </tbody>
                            </table>
                        </td>
                    </tr>
                )
            }
            {
                shouldVirtualizeRows &&
                <tr style={{height: rowV.topSpacerHeight}} aria-hidden/>
            }
            {
                visibleMiddleRows?.map(row => (
                    <DataGridBodyRow
                        key={row.key}
                        row={row}
                        density={density}
                        startScrolling={startScrolling}
                        ref={shouldVirtualizeRows ? row.ref : undefined}
                        {...rowVirtProps}
                    />
                ))
            }
            {
                rowsEnabled && visibleBottomStickyRows.length > 0 && (
                    <tr aria-hidden style={{position: 'sticky', bottom: -4, zIndex: 14, padding: 0}}>
                        <td colSpan={renderedColCount || 1} style={{padding: 0, border: 0}}>
                            <table style={{width: '100%', borderCollapse: 'collapse'}}>
                                <tbody ref={stickyBottomContainerRef}>
                                {visibleBottomStickyRows.map(row => (
                                    <DataGridBodyRow
                                        key={`sticky-bottom-${row.key}`}
                                        row={row}
                                        density={density}
                                        startScrolling={startScrolling}
                                        {...rowVirtProps}
                                    />
                                ))}
                                </tbody>
                            </table>
                        </td>
                    </tr>
                )}
            {
                shouldVirtualizeRows &&
                <tr style={{height: rowV.bottomSpacerHeight}} aria-hidden/>
            }
            {
                columnsEnabled &&
                <ColumnHeightProbe
                    request={activeHeightProbe}
                    createCells={createCells}
                    density={density}
                    minRowHeight={minRowHeight}
                    getColWidth={getColWidth}
                    colSpan={renderedColCount}
                    onMeasured={rowHeightCache.commitMeasurements}
                />
            }
            </tbody>
        </>
    );
};

export default DataGridBody;
